import RefreshIcon from '@mui/icons-material/Refresh';
import React, { useCallback, useEffect, useState } from 'react';
import { Autocomplete, Box, Button, MenuItem, Stack, Tab, Tabs, TextField, Typography, IconButton, Tooltip, Alert } from '@mui/material';
import { aiApi, asArray, dateText, errorMessage } from './aiDeveloperShared';
import { ActionButton, Feedback, Status } from './aiDeveloperComponents';
import { DataTable, PageShell, Section } from './aiDeveloperAcademicUI';
import AIDeveloperDocumentation from './AIDeveloperDocumentation';

const EMPTY_FORM = { ApplicantType: 'Student', Purpose: '', Supervisor: '', AiConfigId: '' };
const TABS = ['Request Access', 'My Applications', 'API Keys', 'Usage', 'Documentation'];

export default function AIDeveloperPortal() {
  const [tab, setTab] = useState(0);
  const [applications, setApplications] = useState([]);
  const [modelRequests, setModelRequests] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [keys, setKeys] = useState([]);
  const [usage, setUsage] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [applicationId, setApplicationId] = useState('');
  const [keyLabel, setKeyLabel] = useState('Developer key');
  const [newSecret, setNewSecret] = useState('');
  const [grants, setGrants] = useState({});
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [busy, setBusy] = useState(false);
  const [eligibility, setEligibility] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const [apps, keyRows, usageRows, modelRows, requests, eligible] = await Promise.all([
        aiApi.get('applications'), aiApi.get('keys'), aiApi.get('usage'), aiApi.get('catalog'), aiApi.get('applications/model-requests'), aiApi.get('eligibility'),
      ]);
      setEligibility(eligible); setApplications(asArray(apps)); setCatalog(asArray(modelRows)); setModelRequests(asArray(requests));
      setKeys(asArray(keyRows));
      setUsage(asArray(usageRows));
    } catch (err) { setError(errorMessage(err)); }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const perform = async (action, message) => {
    setBusy(true); setError(''); setSuccess('');
    try { await action(); await refresh(); if (message) setSuccess(message); }
    catch (err) { setError(errorMessage(err)); }
    finally { setBusy(false); }
  };

  const submit = () => {
    if (!eligibility?.eligible) { setError('Only active students or employees may request access'); return; }
    if (!form.Purpose.trim() || !form.AiConfigId) {
      setError('Purpose and AI model are required'); return;
    }
    perform(async () => { await aiApi.post('applications', { ...form, ApplicantType: account?.ApplicantType || form.ApplicantType }); setForm(EMPTY_FORM); }, 'Model request submitted for review');
  };

  const viewModels = (id) => perform(async () => {
    const rows = await aiApi.get(`applications/${id}/grants`);
    setGrants(previous => ({ ...previous, [id]: asArray(rows) }));
  });

  const createKey = () => {
    if (!applicationId) { setError('Choose an approved application'); return; }
    if (!asArray(grants[applicationId]).length) {
      setError('Load the application models and confirm at least one model grant before creating a key'); return;
    }
    perform(async () => {
      const response = await aiApi.post(`applications/${applicationId}/keys`, { Label: keyLabel.trim() || 'Developer key' });
      setNewSecret(response.ApiKey || '');
    }, 'Key generated. Save it securely now.');
  };

  const revokeKey = (id) => {
    if (!window.confirm('Revoke this API key? Applications using it will stop working.')) return;
    perform(() => aiApi.delete(`keys/${id}`), 'API key revoked');
  };

  const [refreshingTab, setRefreshingTab] = useState(null);
  const [documentationVersion, setDocumentationVersion] = useState(0);
  const refreshCurrentTab = async (index) => {
    if (refreshingTab !== null || busy) return;
    setRefreshingTab(index);
    setError('');
    try {
      if (index === 0) {
        const [models, eligible, apps] = await Promise.all([aiApi.get('catalog'), aiApi.get('eligibility'), aiApi.get('applications')]);
        setCatalog(asArray(models)); setEligibility(eligible); setApplications(asArray(apps));
      }
      if (index === 1) {
        const [apps, requests] = await Promise.all([aiApi.get('applications'), aiApi.get('applications/model-requests')]);
        setApplications(asArray(apps)); setModelRequests(asArray(requests));
      }
      if (index === 2) {
        const [apps, keyRows] = await Promise.all([aiApi.get('applications'), aiApi.get('keys')]);
        setApplications(asArray(apps)); setKeys(asArray(keyRows));
        if (applicationId) {
          const rows = await aiApi.get(`applications/${applicationId}/grants`);
          setGrants(previous => ({ ...previous, [applicationId]: asArray(rows) }));
        }
      }
      if (index === 3) setUsage(asArray(await aiApi.get('usage')));
      if (index === 4) setDocumentationVersion(version => version + 1); // Remount local documentation.
    } catch (err) { setError(errorMessage(err)); }
    finally { setRefreshingTab(null); }
  };
  const account = applications[0];
  const approved = applications.filter(a => a.Status === 'Approved');
  const appColumns = [
    { key: 'ApplicantUser', label: 'Requestor' },
    { key: 'ApplicantType', label: 'Type', filter: 'select' },
    { key: 'Status', label: 'Status', filter: 'select', render: r => <Status value={r.Status} /> },
    { key: 'models', label: 'Approved Models', render: r => <Box>
      <Button size="small" onClick={() => viewModels(r.ApplicationID)}>View Models</Button>
      {grants[r.ApplicationID] && <Typography variant="caption" display="block">{grants[r.ApplicationID].map(g => g.PublicModel).join(', ') || 'None assigned'}</Typography>}
    </Box> },
  ];
  const keyColumns = [
    { key: 'KeyPrefix', label: 'Key Prefix', render: r => `${r.KeyPrefix || ''}…` },
    { key: 'Label', label: 'Label', filter: 'search' },
    { key: 'ExpiresAt', label: 'Expires', render: r => dateText(r.ExpiresAt) },
    { key: 'status', label: 'Status', render: r => <Status value={r.RevokedAt ? 'Revoked' : 'Approved'} /> },
    { key: 'action', label: 'Action', render: r => !r.RevokedAt && <Button color="error" disabled={busy} onClick={() => revokeKey(r.KeyID)}>Revoke</Button> },
  ];

  return <PageShell title="AI Developer Portal" subtitle="Request access, manage your project keys, and monitor usage">
    <Feedback error={error} success={success} clearError={() => setError('')} clearSuccess={() => setSuccess('')} />
    {newSecret && <Alert severity="warning" sx={{ mb: 2 }}>
      <Typography fontWeight={800}>Copy this API key now. It will not be displayed again.</Typography>
      <Typography sx={{ fontFamily: 'monospace', wordBreak: 'break-all', my: 1 }}>{newSecret}</Typography>
      <Button onClick={() => navigator.clipboard.writeText(newSecret)}>Copy</Button>
      <Button onClick={() => setNewSecret('')}>Dismiss</Button>
    </Alert>}
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, borderBottom: '1px solid rgba(25,118,210,0.20)' }}>
      <Tabs value={tab} onChange={(_, value) => setTab(value)} variant="scrollable" scrollButtons="auto"
        sx={{ flex: 1, minWidth: 0, '& .MuiTab-root': { textTransform: 'none', fontWeight: 800 }, '& .Mui-selected': { color: '#F07828 !important' }, '& .MuiTabs-indicator': { backgroundColor: '#F07828', height: 3 } }}>
        {TABS.map(label => <Tab key={label} label={label} />)}
      </Tabs>
      <Tooltip title={`Refresh ${TABS[tab]}`}>
        <span>
          <IconButton size="small" aria-label={`Refresh ${TABS[tab]}`} disabled={refreshingTab !== null || busy}
            onClick={() => refreshCurrentTab(tab)}
            sx={{ flexShrink: 0, mr: 1, color: '#F07828', '&:hover': { bgcolor: '#FFF0E3' } }}>
            <RefreshIcon sx={{ fontSize: 19, animation: refreshingTab === tab ? 'aiTabSpin 0.8s linear infinite' : 'none', '@keyframes aiTabSpin': { to: { transform: 'rotate(360deg)' } } }} />
          </IconButton>
        </span>
      </Tooltip>
    </Box>
    {tab === 0 && <Section title="Apply for AI API Access">
      {eligibility && !eligibility.eligible && <Alert severity="error" sx={{ mb: 2 }}>{eligibility.message}</Alert>}
      {eligibility?.eligible && <Alert severity="info" sx={{ mb: 2 }}>Active {eligibility.userType}: {eligibility.fullName}</Alert>}
      {account?.Status === 'Suspended' && <Alert severity="warning" sx={{ mb: 2 }}>Your access is suspended. If your student account has been reactivated after passing out, you can submit a fresh model request. Administrative suspensions require reinstatement by the campus administrator. Previously revoked API keys cannot be restored.</Alert>}
      <Stack direction={{ xs: 'column', md: 'row' }} gap={2} sx={{ mb: 2 }}>
        <TextField select fullWidth label="Applicant Type" disabled={Boolean(account)} value={account?.ApplicantType || form.ApplicantType} onChange={e => setForm(f => ({ ...f, ApplicantType: e.target.value }))}>
          {['Student', 'Faculty', 'Lab'].map(value => <MenuItem key={value} value={value}>{value}</MenuItem>)}
        </TextField>
        <TextField fullWidth label="Application" value={account ? 'My AI Developer Application' : 'New AI Developer Application'} disabled helperText="One application per campus user; request multiple models under it" />
        <TextField fullWidth label="Faculty Supervisor" value={form.Supervisor} onChange={e => setForm(f => ({ ...f, Supervisor: e.target.value }))} />
      </Stack>
      <TextField select fullWidth sx={{ mb: 2 }} label="Requested AI Model" value={form.AiConfigId}
        onChange={e => setForm(f => ({ ...f, AiConfigId: e.target.value }))}>
        {catalog.map(m => <MenuItem key={m.AiConfigId} value={String(m.AiConfigId)}>{m.DisplayName} — {m.ModelName}</MenuItem>)}
      </TextField>
      <TextField fullWidth multiline minRows={3} label="Project Purpose / Research Description" value={form.Purpose} onChange={e => setForm(f => ({ ...f, Purpose: e.target.value }))} />
      <ActionButton disabled={busy || !eligibility?.eligible} sx={{ mt: 2 }} onClick={submit}>{account?.Status === 'Suspended' ? 'Reapply for Model Access' : account ? 'Request Another Model' : 'Create Application & Request Model'}</ActionButton>
    </Section>}
    {tab === 1 && <Section title="My Application & Model Requests">
      <DataTable columns={appColumns} rows={applications} />
      <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>Model Requests</Typography>
      <DataTable rows={modelRequests} columns={[
        { key: 'RequestedDisplayName', label: 'Model', render: r => `${r.RequestedDisplayName} — ${r.RequestedModelName}` },
        { key: 'Status', label: 'Approval Status', filter: 'select', render: r => <Status value={r.Status} /> },
        { key: 'CurrentStepOrder', label: 'Approval Step' },
      ]} empty="No model requests yet" />
    </Section>}
    {tab === 2 && <Section title="API Keys">
      <Stack direction={{ xs: 'column', md: 'row' }} gap={2} sx={{ mb: 2 }}>
        <TextField select label="Approved Application" value={applicationId} onChange={e => { setApplicationId(e.target.value); setNewSecret(''); }} sx={{ minWidth: 260 }}>
          {approved.map(a => <MenuItem key={a.ApplicationID} value={String(a.ApplicationID)}>My AI Developer Application</MenuItem>)}
        </TextField>
        <TextField label="Key Label" value={keyLabel} onChange={e => setKeyLabel(e.target.value)} />
        <Button disabled={!applicationId || busy} onClick={() => viewModels(applicationId)}>Load Model Grants</Button>
        <ActionButton disabled={!applicationId || busy} onClick={createKey}>Generate API Key</ActionButton>
      </Stack>
      <DataTable columns={keyColumns} rows={keys} />
    </Section>}
    {tab === 3 && <Section title="My Usage"><DataTable rows={usage} columns={[
      { key: 'PublicModel', label: 'Model', filter: 'select' },
      { key: 'Requests', label: 'Requests' }, { key: 'InputTokens', label: 'Input Tokens' }, { key: 'OutputTokens', label: 'Output Tokens' },
    ]} /></Section>}
    {tab === 4 && <Section title="Developer API Documentation">
      <AIDeveloperDocumentation key={documentationVersion} />
    </Section>}
  </PageShell>;
}
